import { useMsal } from '@azure/msal-react';
import { Button, FluentProvider } from '@fluentui/react-components';
import { ShieldCheck } from 'lucide-react';

import AirBridgeMark from './AirBridgeMark';
import TopographyTexture from './TopographyTexture';
import heroBackground from '../assets/hero-background.jpg';
import { TAGLINE, WORDMARK } from '../lib/brand';
import { airbridgeTheme } from '../theme/airbridgeTheme';
import { ACCENT, NAV } from '../theme/tokens';
import { loginRequest } from '../authConfig';

// The AirBridge sign-in screen (FD-458, promoted to the only login in FD-466).
//
// It carries its OWN FluentProvider, and that is not redundant with the root one
// in main.tsx: `UnauthenticatedTemplate` and `AuthenticatedTemplate` are
// siblings under a provider that is itself inside them in some boot states, and
// this screen renders before the app tree settles. Mounting the theme here means
// the sign-in page is branded on the very first paint rather than after MSAL
// resolves.
//
// THE AUTH PATH IS THE EXISTING ONE, UNCHANGED: the same `loginRequest`, the
// same `loginRedirect`, the same error handling the orange login had. A rebrand
// that forked the sign-in flow would be a rebrand that could break sign-in.
//
// The mockup's "Use another account" secondary button is NOT reproduced: MSAL's
// account picker is reached through `prompt: 'select_account'`, which is a
// different request than the one the console uses everywhere else, and shipping
// a second button that silently does the same thing as the first is worse than
// one button that works.

export default function LoginPanel() {
  const { instance } = useMsal();

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((err) => {
      console.error('MSAL login redirect failed', err);
    });
  };

  return (
    <FluentProvider theme={airbridgeTheme}>
      <div
        className="grid min-h-[100dvh] w-full grid-cols-1 lg:grid-cols-[1.15fr_1fr]"
        style={{ backgroundColor: NAV.deep }}
      >
        {/* Hero. `aria-hidden` and a decorative role: it carries mood, not
            information, and a screen reader announcing a stock photograph on a
            sign-in page is noise. Eager, because it is the page. */}
        <div className="relative hidden overflow-hidden lg:block">
          <img
            src={heroBackground}
            alt=""
            aria-hidden="true"
            loading="eager"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(90deg, rgba(6,27,44,0.20) 0%, rgba(6,27,44,0.55) 70%, rgba(6,27,44,0.92) 100%)',
            }}
          />
          <div className="relative flex h-full flex-col justify-end p-12">
            <div
              aria-hidden="true"
              className="mb-6 h-[2px] w-12 rounded"
              style={{ backgroundColor: ACCENT.highlight }}
            />
            <p className="m-0 text-[22px] font-semibold leading-snug text-white">
              Simplify. Migrate. Empower.
            </p>
            <p className="mt-3 max-w-[44ch] text-[14px] leading-relaxed text-white/75">
              {WORDMARK} moves Windows devices between domains and tenants, one
              campaign at a time.
            </p>
          </div>
        </div>

        {/* Sign-in panel.
            `relative overflow-hidden` is what the texture needs: it positions
            the layer and clips the drift and the skew, which both deliberately
            overrun the column's bounds so neither can walk an edge into view.
            The form sits above it on `relative z-10`. */}
        <div className="relative flex items-center justify-center overflow-hidden px-8 py-16">
          <TopographyTexture variant="panel" />
          <div className="relative z-10 w-full max-w-[380px]">
            <div className="mb-10 flex items-center gap-3">
              <AirBridgeMark size={40} />
              <span className="flex flex-col">
                <span className="text-[26px] font-semibold leading-none text-white">
                  {WORDMARK}
                </span>
                <span
                  className="mt-1.5 text-[9px] font-medium uppercase leading-none"
                  style={{ color: NAV.textMuted, letterSpacing: '0.12em' }}
                >
                  {TAGLINE}
                </span>
              </span>
            </div>

            <h1 className="m-0 text-[24px] font-semibold leading-tight text-white">
              Sign in to continue
            </h1>
            <p className="mt-2 text-[13px]" style={{ color: NAV.textMuted }}>
              Access your migration environment with your organizational account.
            </p>

            <Button
              appearance="primary"
              size="large"
              onClick={handleLogin}
              className="mt-8 w-full"
            >
              Sign in with Microsoft
            </Button>

            <div className="mt-10 flex gap-3">
              <ShieldCheck
                size={18}
                strokeWidth={1.5}
                color={ACCENT.highlight}
                aria-hidden="true"
                className="mt-0.5 shrink-0"
              />
              <p className="m-0 text-[12px] leading-relaxed" style={{ color: NAV.textMuted }}>
                <span className="font-semibold text-white">Secure. Compliant. Trusted.</span>{' '}
                {WORDMARK} uses Microsoft Entra ID; your credentials are never handled
                by the console.
              </p>
            </div>
          </div>
        </div>
      </div>
    </FluentProvider>
  );
}
